import Button from 'components/Button';
import ProfileCreateFooter from 'components/Footers/Basic';
import Checkbox from 'components/Form/Checkbox';
import Dropdown from 'components/Form/Dropdown';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import SectionHeader from 'components/SectionHeader';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {FC, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {useLocale} from 'state/locale';
import {jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {SignupRemove} from '../assets/svg';
import {
    CompanyDetailsFormFields,
    UrlParameterOptions,
    UserDetailsFormFields,
} from '../types';
import {UserDetailsBackend} from '../UserDetails';
import {CompanyDetailsFormData, usePostCompanyDetails} from './services';

type CompanyDetailsProps = {
    onBack: () => void;
    onUpdate: (
        formData: Partial<CompanyDetailsFormFields & UserDetailsFormFields>,
    ) => void;
    prefillData?: Partial<CompanyDetailsFormFields & UserDetailsFormFields>;
};

const CompanyDetails: FC<CompanyDetailsProps> = ({
    onBack,
    onUpdate,
    prefillData = {},
}) => {
    const {data} = useBackend<UserDetailsBackend>();

    const methods = useForm<CompanyDetailsFormData>({
        defaultValues: {
            agreedToTerms: prefillData.agreedToTerms,
            companySize: prefillData.companySize,
            numberOfEmployees: prefillData.numberOfEmployees,
            partnerName: prefillData.partnerName,
        },
        shouldUnregister: true,
    });
    const {getValues, handleSubmit, watch} = methods;

    watch('companySize');
    const businessType = getValues('companySize');

    useEffect(() => {
        const subscription = watch((value) => {
            onUpdate(value);
        });

        return () => subscription.unsubscribe();
    });

    const {mutateAsync} = usePostCompanyDetails();

    const languageCode = useLocale();

    const onSubmit = async (formData: CompanyDetailsFormData) => {
        const submittedData: Partial<
            CompanyDetailsFormFields &
                UrlParameterOptions &
                UserDetailsFormFields
        > = {
            agreedToTerms: formData.agreedToTerms,
            companySize: formData.companySize,
            email: prefillData.email,
            languageCode,
            leadSource: data.leadSource,
            nameFirst: prefillData.nameFirst,
            nameLast: prefillData.nameLast,
            numberOfEmployees:
                formData.companySize === 'self_employed'
                    ? '1to4'
                    : formData.numberOfEmployees,
            partnerName: formData.partnerName,
            password: prefillData.password,
            phoneNumber: prefillData.phoneNumber,
            regcode: prefillData.regcode,
            username: prefillData.username,
            utmCampaign: data.utmCampaign,
            utmContent: data.utmContent,
            utmMedium: data.utmMedium,
            utmSource: data.utmSource,
            utmTerm: data.utmTerm,
        };

        mutateAsync(submittedData)
            .then(() => {
                setUrl(window.Urls.profile_setup_wizard_view());
            })
            .catch(onApiFormError(methods));
    };

    const termsOfServiceLink = (
        <a
            href="https://www.makeleaps.jp/利用規約/"
            key="terms-of-service"
            rel="noreferrer"
            target="_blank"
        >
            {t`Terms of Service`}
        </a>
    );

    const privacyPolicyLink = (
        <a
            href="https://www.makeleaps.jp/プライバシーポリシー/"
            key="privacy-policy"
            rel="noreferrer"
            target="_blank"
        >
            {t`Privacy Policy`}
        </a>
    );

    return (
        <div className="relative flex min-h-screen flex-col bg-grey-100 ">
            <HeaderBasic className="px-8" />
            <DualPage
                pagination={{
                    current: 2,
                    length: 2,
                }}
                size="6xl"
            >
                <FormProvider {...methods}>
                    <form
                        className="rounded border border-grey-300 bg-white p-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            title={t`Create Your Free Account`}
                        />
                        <Stacked className="mt-4 w-full">
                            <Radio
                                isHorizontal={true}
                                label={t`Business Type`}
                                name="companySize"
                                options={[
                                    {
                                        label: (
                                            <div className="flex items-center">
                                                {t`Organization`}
                                                <TooltipIcon
                                                    aria-label="organization-info"
                                                    className="ml-1 inline-block"
                                                    message={t`Corporation, LLC, etc.`}
                                                    size="base"
                                                />
                                            </div>
                                        ),
                                        value: 'organization',
                                    },
                                    {
                                        label: t`Self-employed`,
                                        value: 'self_employed',
                                    },
                                ]}
                                required={true}
                            />
                            <InputText
                                label={t`Company name or Business name`}
                                name="partnerName"
                                required={true}
                            />
                            {businessType === 'organization' && (
                                <Dropdown
                                    aria-label="Number of employees"
                                    label={t`Numbers of employees`}
                                    name="numberOfEmployees"
                                    options={[
                                        {
                                            label: t`1 to 4 employees`,
                                            value: '1to4',
                                        },
                                        {
                                            label: t`5 to 20 employees`,
                                            value: '5to20',
                                        },
                                        {
                                            label: t`21 to 50 employees`,
                                            value: '21to50',
                                        },
                                        {
                                            label: t`51 to 100 employees`,
                                            value: '51to100',
                                        },
                                        {
                                            label: t`101 to 300 employees`,
                                            value: '101to300',
                                        },
                                        {
                                            label: t`301 to 1000 employees`,
                                            value: '301to1000',
                                        },
                                        {
                                            label: t`More than 1001 employees`,
                                            value: '1001',
                                        },
                                    ]}
                                    required={true}
                                    type="select"
                                />
                            )}
                            <div>
                                <Checkbox
                                    label={jt`I agree to MakeLeaps' ${termsOfServiceLink} and I have read the ${privacyPolicyLink}`}
                                    name="agreedToTerms"
                                    rules={{
                                        required: t`Please complete this required field.`,
                                    }}
                                />
                            </div>
                            <FormResult />
                            <FormActions>
                                <Button kind="tertiary" onClick={onBack}>
                                    {t`Go back`}
                                </Button>
                                <SubmitButton>{t`Create Account`}</SubmitButton>
                            </FormActions>
                        </Stacked>
                    </form>
                </FormProvider>
                <div className="p-6">
                    <SectionHeader
                        className="mt-0"
                        hideHR={true}
                        title={t`Manage Your Invoices - Anywhere, Anytime`}
                    />
                    <section className="mt-6 flex">
                        <SignupRemove className="w-40 flex-none" />
                        <div>
                            <p className="m-0">
                                {t`We help customers in every industry improve their business. Create and send invoices all through the cloud without the restrictions of place and time.`}
                            </p>
                        </div>
                    </section>
                </div>
            </DualPage>
            <ProfileCreateFooter />
        </div>
    );
};

export default CompanyDetails;
